import { Text } from '../atoms/text';
import { classes } from '../../utils/styles.util';
import type { NamedOption } from './named-option-single-select';

/**
 * Props for the named toggle-list control.
 */
interface NamedOptionToggleListProps {
  /** Field title shown above the toggle list. */
  title: string;
  /** Options available for toggling. */
  options: NamedOption[];
  /** Whether the options are currently loading. */
  loading: boolean;
  /** Set of selected option ids. */
  selectedIds: number[];
  /** Empty-state message shown when there are no options. */
  emptyMessage: string;
  /** Loading message shown while options are being fetched. */
  loadingMessage: string;
  /** Called when one option should be toggled. */
  onToggle: (optionId: number) => void;
}

/**
 * Shared multi-select control rendered as toggle pills.
 */
export const NamedOptionToggleList: React.FC<NamedOptionToggleListProps> = ({
  title,
  options,
  loading,
  selectedIds,
  emptyMessage,
  loadingMessage,
  onToggle,
}) => (
  <div className="space-y-1">
    <Text
      as="span"
      size="tiny"
      weight="medium"
      className="uppercase tracking-wide text-neutral-500"
    >
      {title}
    </Text>
    <div className="flex flex-wrap gap-2 rounded-xl border border-neutral-200 bg-neutral-50 p-3">
      {loading ? (
        <Text size="small" className="text-neutral-500">
          {loadingMessage}
        </Text>
      ) : options.length === 0 ? (
        <Text size="small" className="text-neutral-500">
          {emptyMessage}
        </Text>
      ) : (
        options.map((option) => {
          const selected = selectedIds.includes(option.id);
          return (
            <button
              key={option.id}
              type="button"
              onClick={() => onToggle(option.id)}
              className={classes(
                'rounded-full border px-3 py-1 text-sm transition',
                selected
                  ? 'border-primary-900 bg-primary-900 text-white'
                  : 'border-neutral-200 bg-white text-neutral-700 hover:border-neutral-300'
              )}
            >
              {option.name}
            </button>
          );
        })
      )}
    </div>
  </div>
);
